"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useEffect, useState, useRef } from "react";
import { Menu, X, Search, ChevronDown, ChevronRight, LogOut } from "lucide-react";
import { cn } from "@/lib/utils";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu, MenuItem } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink } from "@/components/ui/navigation-menu";
import { Button } from "@/components/ui/button";
import { CartIcon } from "@/components/shop/CartIcon";
import { ProductsSearch } from "@/components/shop/ProductsSearch";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
interface HeaderMenuConsumerProps {
  searchQuery: string;
  setSearchQuery: (query: string) => void;
}
function HeaderMenuConsumer({
  searchQuery,
  setSearchQuery
}: HeaderMenuConsumerProps) {
  const {
    menuItems,
    loading
  } = useMenu();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [expandedMobileItems, setExpandedMobileItems] = useState<Record<string, boolean>>({});
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (buttonRef.current && buttonRef.current.contains(target)) return;
      if (isMenuOpen && menuRef.current && !menuRef.current.contains(target)) {
        setIsMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isMenuOpen]);
  if (loading) {
    return <div className="flex gap-6">
      <div className="h-5 w-16 bg-muted/20 animate-pulse rounded-md" />
      <div className="h-5 w-20 bg-muted/20 animate-pulse rounded-md" />
      <div className="h-5 w-16 bg-muted/20 animate-pulse rounded-md" />
    </div>;
  }
  const linkClasses = "relative text-sm font-default font-medium text-foreground py-2 transition-colors hover:text-primary after:absolute after:bottom-0 after:left-1/2 after:h-[2px] after:w-0 after:-translate-x-1/2 after:bg-primary after:transition-all after:duration-300 hover:after:w-full block lg:inline-block";
  const toggleMobileItem = (id: string) => {
    setExpandedMobileItems(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };
  const renderMenuItem = (item: MenuItem, index: number, isChild = false) => {
    const hasChildren = item.children && item.children.length > 0;
    if (!hasChildren) {
      return <li key={item.id} data-index={index} className="list-none">
        <NavigationMenuLink asChild>
          <Link to={item.href || "#"} className={cn(linkClasses, isChild && "after:hidden py-1.5 px-3 hover:bg-primary/10 rounded-md")} onClick={() => setIsMenuOpen(false)}>
            {item.label}
          </Link>
        </NavigationMenuLink>
      </li>;
    }
    return <li key={item.id} data-index={index} className={cn("relative list-none group", !isChild ? "lg:h-full flex items-center" : "")} onMouseEnter={() => !isChild && setOpenDropdown(item.id)}>
      <div className="hidden lg:flex items-center gap-1 cursor-default">
        <NavigationMenuLink asChild>
          <Link to={item.href || "#"} className={linkClasses} onClick={() => setIsMenuOpen(false)}>
            {item.label}
          </Link>
        </NavigationMenuLink>
        <ChevronDown className="size-3 text-foreground/50 group-hover:text-primary transition-colors" />
        <div className={cn("absolute top-full left-0 min-w-[200px] bg-background/95 backdrop-blur-md border border-primary/20 shadow-xl rounded-md p-2 z-50 opacity-0 translate-y-2 pointer-events-none transition-all duration-200 group-hover:opacity-100 group-hover:translate-y-0 group-hover:pointer-events-auto", isChild && "left-full top-0 ml-1")}>
          <ul className="flex flex-col gap-1">
            {item.children?.map((child, i) => renderMenuItem(child, i, true))}
          </ul>
        </div>
      </div>

      <div className="lg:hidden w-full">
        <div className="flex items-center justify-between w-full">
          <Link to={item.href || "#"} className={linkClasses} onClick={() => setIsMenuOpen(false)}>
            {item.label}
          </Link>
          <Button variant="ghost" size="icon" className="size-8" onClick={() => toggleMobileItem(item.id)}>
            {expandedMobileItems[item.id] ? <ChevronDown className="size-4" /> : <ChevronRight className="size-4" />}
          </Button>
        </div>
        {expandedMobileItems[item.id] && <ul className="flex flex-col pl-4 mt-2 border-l border-primary/20 gap-2">
          {item.children?.map((child, i) => renderMenuItem(child, i, true))}
        </ul>}
      </div>
    </li>;
  };
  return <>
    <div ref={menuRef} id="header-menu-container" className={cn("absolute top-20 left-0 w-full bg-background border-b border-primary/20 p-4 lg:static lg:block lg:w-auto lg:border-none lg:p-0 transition-all duration-300 z-50", isMenuOpen ? "block animate-in fade-in slide-in-from-top-2" : "hidden lg:block")}>
      <NavigationMenu viewport={false}>
        <ul className="flex flex-col lg:flex-row lg:items-center gap-4 lg:gap-8 list-none">
          {menuItems.map((item, i) => renderMenuItem(item, i))}
        </ul>
      </NavigationMenu>
      <div className="mt-6 lg:hidden">
        <ProductsSearch value={searchQuery} onChange={setSearchQuery} placeholder="Search" className="w-full p-3 text-sm bg-[#2C2C30] border-[#47474F] rounded-md [&_input]:bg-transparent [&_input]:border-none [&_input]:text-foreground [&_input::placeholder]:text-[#CCCCCC]" />
      </div>
    </div>

    <div className="lg:hidden z-50 relative">
      <Button ref={buttonRef} variant="ghost" size="icon" aria-label={isMenuOpen ? "Close menu" : "Open menu"} className="hover:bg-primary/10" onClick={e => {
        e.stopPropagation();
        setIsMenuOpen(prev => !prev);
      }} aria-expanded={isMenuOpen}>
        {isMenuOpen ? <X className="size-6 text-foreground" /> : <Menu className="size-6 text-foreground" />}
      </Button>
    </div>
  </>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [username, setUsername] = useState("Player");
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [walletBalance, setWalletBalance] = useState("0.00");
  const dropdownRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (window.p2wCurrentUser) {
      setIsLoggedIn(window.p2wCurrentUser.isLoggedIn);
      if (window.p2wCurrentUser.username) {
        setUsername(window.p2wCurrentUser.username);
      }
      if (window.p2wCurrentUser.wallet_balance) {
        setWalletBalance(window.p2wCurrentUser.wallet_balance);
      } else if (window.p2wCurrentUser.balance) {
        setWalletBalance(window.p2wCurrentUser.balance);
      }
    }
  }, []);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowUserDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 0);
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  const handleLogoutClick = () => {
    setShowUserDropdown(false);
    setShowLogoutModal(true);
  };
  const confirmLogout = () => {
    setShowLogoutModal(false);
    const logoutUrl = window.p2wCurrentUser?.logoutUrl || "/my-account/customer-logout/?redirect_to=/";
    window.location.href = logoutUrl;
  };
  const cancelLogout = () => setShowLogoutModal(false);
  return <section id="đầu-trang" className={cn("sticky top-0 z-50 w-full transition-all duration-300 border-b", isScrolled ? "bg-background/80 backdrop-blur-xl border-primary/40 shadow-lg shadow-primary/10" : "bg-background border-transparent")}>
      
      <header className="container mx-auto px-4 lg:px-8 h-20 flex items-center justify-between relative">
        
        <div className="flex-none flex items-center justify-start z-10 gap-2 w-auto lg:gap-8">
          <div className="flex-none">
            <Link to="/" className="block">
              <img src="https://p2wstore.com/wp-content/uploads/2026/04/logop2w2.png" alt="P2W" className="h-7 object-contain !w-[70px]" />
            </Link>
          </div>
          <MenuProvider menu_id="47">
            <HeaderMenuConsumer searchQuery={searchQuery} setSearchQuery={setSearchQuery} />
          </MenuProvider>
          <div className="hidden flex-1 max-w-sm md:block sm:max-w-[200px]">
            <div className="relative group w-full">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 size-3.5 text-[#CCCCCC] z-20 pointer-events-none" />
              <ProductsSearch value={searchQuery} onChange={setSearchQuery} placeholder="Search" className="w-full bg-[#2C2C30] border-[#47474F] rounded-md [&_input]:pl-8 [&_input]:h-8 [&_input]:text-xs [&_input]:bg-transparent [&_input]:border-none [&_input]:text-foreground [&_input::placeholder]:text-[#CCCCCC]" />
            </div>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <CartIcon variant="ghost" size="icon" iconSize="size-5" className="text-foreground hover:text-primary transition-colors" />

          {isLoggedIn ? <div className="relative" ref={dropdownRef}>
              <button onClick={() => setShowUserDropdown(!showUserDropdown)} className="h-[28px] inline-flex items-center gap-2 whitespace-nowrap text-sm font-semibold px-5 py-1 rounded-2xl 
                           bg-gradient-to-br from-[#67E8F9] to-[#1E40AF] 
                           hover:from-[#1E40AF] hover:to-[#67E8F9]
                           text-white transition-all shadow-xs">
                <SafeHydrate><Avatar className="size-6 border border-white/30">
                  <AvatarFallback className="bg-white/20 text-white text-xs font-medium">
                    {username[0]?.toUpperCase() || "U"}
                  </AvatarFallback>
                </Avatar></SafeHydrate>
                <span className="hidden md:block">{username}</span>
                <ChevronDown className="size-4" />
              </button>

              {showUserDropdown && <div className="absolute right-0 mt-2 w-56 bg-background border border-border rounded-xl shadow-xl py-2 z-50 overflow-hidden">
                  <ul className="text-sm">
                    {/* KHỐI CREDIT ĐÃ ĐƯỢC CHUYỂN THÀNH 1 HÀNG TIẾNG ANH CHUẨN */}
                    <li className="px-4 py-2.5 border-b border-border bg-muted/40 mb-1 flex flex-row items-center gap-1.5 whitespace-nowrap">
                      <span className="text-muted-foreground font-medium">P2W Credit:</span>
                      <span className="font-bold text-emerald-400">{walletBalance} USD</span>
                    </li>

                    <li><Link to="/my-account/?tab=overview" className="block px-4 py-2.5 hover:bg-accent" onClick={() => setShowUserDropdown(false)}>Overview</Link></li>
                    <li><Link to="/my-account/?tab=orders" className="block px-4 py-2.5 hover:bg-accent" onClick={() => setShowUserDropdown(false)}>Orders</Link></li>
                    <li><Link to="/my-account/?tab=account" className="block px-4 py-2.5 hover:bg-accent" onClick={() => setShowUserDropdown(false)}>Account</Link></li>
                    <li><Link to="/my-account/?tab=settings" className="block px-4 py-2.5 hover:bg-accent" onClick={() => setShowUserDropdown(false)}>Settings</Link></li>
                    <li className="border-t border-border mt-1">
                      <button onClick={handleLogoutClick} className="block w-full text-left px-4 py-2.5 text-red-400 hover:bg-accent">
                        Logout
                      </button>
                    </li>
                  </ul>
                </div>}
            </div> : <Button asChild className="h-[28px] inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-semibold px-5 py-1 rounded-2xl bg-gradient-to-br from-[#67E8F9] to-[#1E40AF] hover:from-[#1E40AF] hover:to-[#67E8F9] text-white transition-all shadow-xs">
              <Link to="/login">Login</Link>
            </Button>}
        </div>
      </header>

      {/* Modal Logout */}
      {showLogoutModal && <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[9999]">
          <div className="bg-background border border-primary/30 rounded-3xl p-8 max-w-sm w-full mx-4 shadow-2xl">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-6 bg-red-500/10 rounded-2xl flex items-center justify-center">
                <LogOut className="size-8 text-red-400" />
              </div>
              <h3 className="font-serif text-2xl font-bold text-foreground mb-2">Log Out?</h3>
              <p className="text-muted-foreground mb-8">Are you sure you want to log out of your P2W account?</p>
              <div className="flex gap-3">
                <Button onClick={cancelLogout} variant="outline" className="flex-1 h-12 text-base font-medium">Cancel</Button>
                <Button onClick={confirmLogout} className="flex-1 h-12 bg-red-500 hover:bg-red-600 text-base font-medium">Log Out Now</Button>
              </div>
            </div>
          </div>
        </div>}
    </section>;
}